// TODO(#686): This step is a placeholder until the native availability
// management components ship. When #686 lands, replace the weekly-block UI
// below with the shared availability editor and migrate step_data[7] to the
// richer model (array of blocks per day + overrides).

import { useMemo, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useOnboarding, useForwardAction } from '@/components/onboarding'
import {
  availabilityStepDataSchema,
  WEEKDAYS,
} from '@/schemas/onboarding-availability'
import type {
  AvailabilityStepData,
  WeekdayKey,
} from '@/types/onboarding'

const STEP_INDEX = 7

type WeeklyMap = AvailabilityStepData['weekly']

function defaultWeekly(): WeeklyMap {
  const defaultBlock = { start_time: '09:00', end_time: '17:00' }
  return {
    monday: { ...defaultBlock },
    tuesday: { ...defaultBlock },
    wednesday: { ...defaultBlock },
    thursday: { ...defaultBlock },
    friday: { ...defaultBlock },
    saturday: null,
    sunday: null,
  }
}

function getStoredData(
  stepData: Record<string, unknown>,
): AvailabilityStepData | null {
  const stored = stepData[STEP_INDEX]
  if (!stored) return null
  const parsed = availabilityStepDataSchema.safeParse(stored)
  return parsed.success ? parsed.data : null
}

interface DayValidation {
  hasAny: boolean
  errors: Partial<Record<WeekdayKey, string>>
}

function validate(weekly: WeeklyMap): DayValidation {
  let hasAny = false
  const errors: Partial<Record<WeekdayKey, string>> = {}
  for (const { key } of WEEKDAYS) {
    const block = weekly[key]
    if (!block) continue
    hasAny = true
    if (block.start_time >= block.end_time) {
      errors[key] = 'End time must be after start time.'
    }
  }
  return { hasAny, errors }
}

export function AvailabilityStep() {
  const { state, setStepData, markCompleted, goToStep } = useOnboarding()

  const [weekly, setWeekly] = useState<WeeklyMap>(() => {
    const stored = getStoredData(state.stepData)
    return stored?.weekly ?? defaultWeekly()
  })

  const { hasAny, errors } = useMemo(() => validate(weekly), [weekly])
  const hasErrors = Object.keys(errors).length > 0

  const toggleDay = (day: WeekdayKey, available: boolean) => {
    setWeekly((prev) => ({
      ...prev,
      [day]: available ? { start_time: '09:00', end_time: '17:00' } : null,
    }))
  }

  const setTime = (
    day: WeekdayKey,
    field: 'start_time' | 'end_time',
    value: string,
  ) => {
    setWeekly((prev) => {
      const block = prev[day]
      if (!block) return prev
      return { ...prev, [day]: { ...block, [field]: value } }
    })
  }

  const onSubmit = () => {
    setStepData(STEP_INDEX, { weekly } satisfies AvailabilityStepData)
    markCompleted(STEP_INDEX)
    goToStep(STEP_INDEX + 1)
  }

  useForwardAction({
    label: 'Continue',
    disabled: !hasAny || hasErrors,
    onClick: onSubmit,
  })

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-slate-900">Availability</h2>
        <p className="mt-1 text-sm text-slate-600">
          Set your recurring weekly hours. You&apos;ll be able to refine this and add
          date-specific exceptions after go live.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Weekly hours</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {WEEKDAYS.map(({ key, label }) => {
            const block = weekly[key]
            const available = block !== null
            const error = errors[key]
            return (
              <div key={key} className="space-y-2">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id={`availability-${key}`}
                    checked={available}
                    onCheckedChange={(val) => toggleDay(key, val === true)}
                  />
                  <Label
                    htmlFor={`availability-${key}`}
                    className="w-28 text-sm font-medium leading-none"
                  >
                    {label}
                  </Label>
                  {available && block ? (
                    <div className="flex flex-1 items-center gap-2">
                      <Input
                        aria-label={`${label} start time`}
                        type="time"
                        value={block.start_time}
                        onChange={(e) => setTime(key, 'start_time', e.target.value)}
                        className="max-w-28"
                      />
                      <span className="text-sm text-slate-500">to</span>
                      <Input
                        aria-label={`${label} end time`}
                        type="time"
                        value={block.end_time}
                        onChange={(e) => setTime(key, 'end_time', e.target.value)}
                        className="max-w-28"
                      />
                    </div>
                  ) : (
                    <span className="text-sm text-slate-400">Unavailable</span>
                  )}
                </div>
                {error && (
                  <p role="alert" className="ml-8 text-sm text-red-600">
                    {error}
                  </p>
                )}
              </div>
            )
          })}
          {!hasAny && (
            <p className="text-sm text-slate-500">
              Add at least one day to continue.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
